import styles from './priceListProductsTable.module.css'
import { useEffect, useMemo, useRef, useState } from "react"
import { Action, Column, Data, FieldType, FieldValue, FilterField, Filters, InputGroup, O, option, Section, Table, TextOrNumber } from "../../../../../features/ui"
import { Product, ProductName } from "../../../../products"
import { usePricesHooks } from "../../../../operations"
import { useStateHooks } from "../../../../../state"
import { TableName } from "../../../../../tables"
import { ProductYield } from "../../../state/types"
import { isDefined, normalize } from "../../../../../utils/typeUtils"
import { isSameYield } from "../customerTypeUtils"

const PAGE_SIZE = 10

enum YieldFilter {
    GENERAL = 'general',
    PARTICULAR = 'particular'
}

type Props = {
    productYields: ProductYield[]
    generalYield: number
    baseCustomerTypeId?: string
    onChangeYield: (productId: string, productYield: number) => void
    onResetYield: (productId: string) => void
    onEditItem: (productId: string) => void
}

type Row = {
    productId: string
    name: string
    particular: boolean
    yield: number
    price: number
    priceVat: number
    basePrice: number
    basePriceVat: number
}

export const PriceListProductsTable = (props: Props) => {
    const { productYields, generalYield, baseCustomerTypeId, onChangeYield, onResetYield, onEditItem } = props

    const [searchTerm, setSearchTerm] = useState("")
    const [yieldFilter, setYieldFilter] = useState<O<YieldFilter>>()
    // Productos editados con un filtro activo: se mantienen visibles aunque dejen de cumplirlo
    const [stickyIds, setStickyIds] = useState<string[]>([])
    const [page, setPage] = useState(1)
    // Input a enfocar cuando se renderice la página a la que se llegó con Tab
    const pendingFocusId = useRef<string>()

    const pricesHooks = usePricesHooks()
    const { getList, find } = useStateHooks()

    useEffect(() => {
        setStickyIds([])
        setPage(1)
    }, [searchTerm, yieldFilter])

    useEffect(() => {
        if (!pendingFocusId.current) return
        focusInput(pendingFocusId.current)
        pendingFocusId.current = undefined
    }, [page])

    const products = getList(TableName.PRODUCTS, true) as Product[]
    const excludedIds = useMemo(() => productYields.filter(py => py.isExcluded).map(py => py.productId), [productYields])
    // Un ajuste igual al general no cuenta como particular (y no se guarda como tal)
    const particularYields = useMemo(() => new Map(
        productYields
            .filter(py => !py.isExcluded && !isSameYield(py.yield, generalYield))
            .map(py => [py.productId, py.yield])
    ), [productYields, generalYield])

    const allRows = useMemo(() => products
        .filter(product => !excludedIds.includes(product.id) && find(TableName.PRODUCT_PRICES, product.id, 'productId'))
        .map((product): Row => {
            const particularYield = particularYields.get(product.id)
            const particular = isDefined(particularYield)
            const currentYield = particular ? particularYield! : generalYield
            const basePrice = pricesHooks.product.adjustedPrice(product.id, false, baseCustomerTypeId)
            const basePriceVat = pricesHooks.product.adjustedPrice(product.id, true, baseCustomerTypeId)
            return {
                productId: product.id,
                name: product.name,
                particular,
                yield: currentYield,
                price: pricesHooks.adjustment.apply(basePrice, currentYield),
                priceVat: pricesHooks.adjustment.apply(basePriceVat, currentYield),
                basePrice,
                basePriceVat
            }
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    , [products, excludedIds, particularYields, generalYield, baseCustomerTypeId])

    const rows = useMemo(() => {
        const search = normalize(searchTerm)
        return allRows.filter(row => {
            if (stickyIds.includes(row.productId)) return true
            if (search && !normalize(row.name).includes(search)) return false
            if (yieldFilter === YieldFilter.GENERAL) return !row.particular
            if (yieldFilter === YieldFilter.PARTICULAR) return row.particular
            return true
        })
    }, [allRows, searchTerm, yieldFilter, stickyIds])

    // Cada columna editable recibe el productId como valor para poder ubicar su fila
    const tableRows = useMemo(() => rows.map(row => ({
        productId: row.productId,
        yield: row.productId,
        price: row.productId,
        priceVat: row.productId
    }) as Data), [rows])

    const findRow = (productId: string) => allRows.find(row => row.productId === productId)!

    const changeYield = (productId: string, newYield: number) => {
        if ((searchTerm || yieldFilter) && !stickyIds.includes(productId)) {
            setStickyIds(prev => [...prev, productId])
        }
        onChangeYield(productId, newYield)
    }

    // Los campos también notifican cambios al montarse o al recalcularse: solo se reacciona a los del usuario
    const onChangeYieldField = (productId: string) => (value?: TextOrNumber, manualChange?: boolean) => {
        if (!manualChange || !isDefined(value)) return
        changeYield(productId, value as number)
    }

    const onChangePriceField = (productId: string, withVat: boolean) => (value?: number, manualChange?: boolean) => {
        const row = findRow(productId)
        const basePrice = withVat ? row.basePriceVat : row.basePrice
        if (!manualChange || !isDefined(value) || value! <= 0 || basePrice <= 0) return
        changeYield(productId, pricesHooks.adjustment.calculateYield(value!, basePrice))
    }

    const focusInput = (inputId: string) => {
        const input = document.getElementById(inputId) as O<HTMLInputElement>
        input?.focus()
        input?.select()
    }

    // Tab / Shift+Tab recorren la misma columna hacia abajo / arriba, pasando de página al llegar al borde
    const onCellKeyDown = (column: string, productId: string) => (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab') return
        const targetIndex = rows.findIndex(row => row.productId === productId) + (e.shiftKey ? -1 : 1)
        const targetRow = rows[targetIndex]
        // Primera o última fila de toda la lista: Tab por defecto
        if (!targetRow) return
        e.preventDefault()
        const targetInputId = `${column}-${targetRow.productId}`
        const targetPage = Math.floor(targetIndex / PAGE_SIZE) + 1
        if (targetPage === page) {
            focusInput(targetInputId)
        } else {
            pendingFocusId.current = targetInputId
            setPage(targetPage)
        }
    }

    const renderCell = (column: string, productId: string, content: JSX.Element, className = '') => (
        <div
            className={`${className} ${findRow(productId).particular ? '' : styles.inherited}`}
            onKeyDown={onCellKeyDown(column, productId)}
        >
            {content}
        </div>
    )

    // Como PriceField pero sin el "ARS", para que la tabla entre sin apretar la columna de producto
    const renderPriceField = (id: string, value: number, onChange: (value?: number, manualChange?: boolean) => void) => (
        <InputGroup
            id={id}
            value={value}
            preLabel="$"
            min={0.01}
            optional
            fit
            thousandsSeparator
            className={styles.priceField}
            onChange={onChange as (value?: TextOrNumber) => void}
        />
    )

    const columns: Column[] = [
        {
            name: 'productId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (value?: FieldValue) => (
                <div className={styles.productCell}>
                    <ProductName product={find(TableName.PRODUCTS, value as string) as O<Product>} />
                </div>
            )
        },
        {
            name: 'yield',
            type: FieldType.PERCENTAGE,
            label: 'Ajuste',
            render: (value?: FieldValue) => {
                const productId = value as string
                return renderCell('yield', productId, (
                    <InputGroup
                        id={`yield-${productId}`}
                        value={findRow(productId).yield}
                        postLabel="%"
                        min={-100}
                        optional
                        fit
                        className={styles.yieldField}
                        onChange={onChangeYieldField(productId)}
                    />
                ))
            }
        },
        {
            name: 'price',
            type: FieldType.PRICE,
            label: 'Precio sin IVA',
            render: (value?: FieldValue) => {
                const productId = value as string
                return renderCell('price', productId, renderPriceField(`price-${productId}`, findRow(productId).price, onChangePriceField(productId, false)))
            }
        },
        {
            name: 'priceVat',
            type: FieldType.PRICE,
            label: 'Precio con IVA',
            render: (value?: FieldValue) => {
                const productId = value as string
                return renderCell('priceVat', productId, renderPriceField(`priceVat-${productId}`, findRow(productId).priceVat, onChangePriceField(productId, true)))
            }
        }
    ]

    const getRowActions = (productId: string): Action[] => {
        const actions: Action[] = [
            { icon: 'pencil-fill', kind: 'tertiary', tooltip: 'Editar', onClick: () => onEditItem(productId) }
        ]
        if (findRow(productId)?.particular) {
            actions.push({
                icon: 'arrow-counterclockwise',
                kind: 'tertiary',
                tooltip: 'Volver al ajuste general',
                onClick: () => onResetYield(productId)
            })
        }
        return actions
    }

    const yieldFilterOptions = useMemo(() => [
        option(YieldFilter.GENERAL, 'Con ajuste general'),
        option(YieldFilter.PARTICULAR, 'Con ajuste particular')
    ], [])

    const filterFields: FilterField[] = useMemo(() => [
        {
            name: 'yieldFilter',
            type: FieldType.SELECT,
            label: 'Ajuste',
            placeholder: 'Todos',
            options: yieldFilterOptions,
            optional: true,
            onChange: (value?: FieldValue) => setYieldFilter(value as O<YieldFilter>)
        }
    ], [yieldFilterOptions])

    return (
        <Section id='productAdjustments' title='Ajuste por Producto' space={12} defaultExpanded className={styles.section}>
            <Filters
                filtersKey="price-list-products-search"
                searchField={{
                    name: 'search',
                    type: FieldType.TEXT,
                    placeholder: 'Buscar producto...',
                    icon: 'search',
                    value: searchTerm,
                    optional: true,
                    onChange: (val) => setSearchTerm((val as string) || '')
                }}
                fields={filterFields}
            />
            <div className={styles.tableContainer}>
                <Table
                    columns={columns}
                    rows={tableRows}
                    rowId='productId'
                    actions={getRowActions}
                    pageSize={PAGE_SIZE}
                    page={page}
                    onPageChange={setPage}
                />
            </div>
        </Section>
    )
}
